// core/action/manufacturingAction.js
// Complete Manufacturing Actions with Raw Materials Support

import { manufacturingAPI, rawMaterialAPI } from './api';

export const manufacturingActions = {

    // ==================== RAW MATERIAL DISPATCH ====================
    getDispatches: async (params = {}) => {
        try {
            const response = await manufacturingAPI.getDispatches(params);
            if (response?.success === true) {
                return {
                    success: true,
                    data: response.data || [],
                    total: response.total || 0,
                    page: response.page || 1,
                    totalPages: response.total_pages || response.totalPages || 1,
                };
            }
            return { success: false, message: response?.message || 'Failed to fetch dispatches', data: [] };
        } catch (error) {
            console.error('Error in getDispatches:', error);
            return { success: false, message: error.message || 'Failed to fetch dispatches', data: [] };
        }
    },

    getDispatch: async (id) => {
        try {
            const response = await manufacturingAPI.getDispatch(id);
            if (response?.success === true) {
                return { success: true, data: response.data };
            }
            return { success: false, message: response?.message || 'Failed to fetch dispatch' };
        } catch (error) {
            console.error('Error in getDispatch:', error);
            return { success: false, message: error.message || 'Failed to fetch dispatch' };
        }
    },

    createDispatch: async (dispatchData) => {
        try {
            // Validate required fields
            if (!dispatchData.dispatch_date) {
                return { success: false, message: 'Dispatch date is required' };
            }
            if (!dispatchData.to_location) {
                return { success: false, message: 'Destination location is required' };
            }
            if (!dispatchData.items || dispatchData.items.length === 0) {
                return { success: false, message: 'At least one item is required' };
            }

            // Validate each item
            for (const item of dispatchData.items) {
                if (!item.material_id || item.material_id <= 0) {
                    return { success: false, message: 'Raw material selection is required for all items' };
                }
                if (!item.quantity || item.quantity <= 0) {
                    return { success: false, message: 'Quantity must be greater than 0 for all items' };
                }
            }

            const response = await manufacturingAPI.createDispatch(dispatchData);

            if (response?.success === true) {
                return {
                    success: true,
                    data: response.data,
                    message: response.message || 'Raw material dispatched successfully'
                };
            }

            return {
                success: false,
                message: response?.message || 'Failed to create dispatch'
            };
        } catch (error) {
            console.error('Error in createDispatch:', error);
            return {
                success: false,
                message: error.response?.data?.message || error.message || 'Failed to create dispatch'
            };
        }
    },

    updateDispatchStatus: async (id, status) => {
        try {
            const response = await manufacturingAPI.updateDispatchStatus(id, status);
            if (response?.success === true) {
                return { success: true, message: response.message || 'Status updated successfully' };
            }
            return { success: false, message: response?.message || 'Failed to update status' };
        } catch (error) {
            console.error('Error in updateDispatchStatus:', error);
            return { success: false, message: error.message || 'Failed to update status' };
        }
    },


    // ==================== RAW MATERIAL RECEIVE ====================
    getReceives: async (params = {}) => {
        try {
            const response = await manufacturingAPI.getReceives(params);
            if (response?.success === true) {
                return {
                    success: true,
                    data: response.data || [],
                    total: response.total || 0,
                    page: response.page || 1,
                    totalPages: response.total_pages || response.totalPages || 1,
                };
            }
            return { success: false, message: response?.message || 'Failed to fetch receives', data: [] };
        } catch (error) {
            console.error('Error in getReceives:', error);
            return { success: false, message: error.message || 'Failed to fetch receives', data: [] };
        }
    },

    getReceive: async (id) => {
        try {
            const response = await manufacturingAPI.getReceive(id);
            if (response?.success === true) {
                return { success: true, data: response.data };
            }
            return { success: false, message: response?.message || 'Failed to fetch receive' };
        } catch (error) {
            console.error('Error in getReceive:', error);
            return { success: false, message: error.message || 'Failed to fetch receive' };
        }
    },

    createReceive: async (receiveData) => {
        try {
            if (!receiveData.dispatch_id) {
                return { success: false, message: 'Dispatch ID is required' };
            }
            if (!receiveData.receive_date) {
                return { success: false, message: 'Receive date is required' };
            }
            if (!receiveData.items || receiveData.items.length === 0) {
                return { success: false, message: 'At least one item is required' };
            }

            const response = await manufacturingAPI.createReceive(receiveData);
            if (response?.success === true) {
                return { success: true, data: response.data, message: response.message || 'Materials received successfully' };
            }
            return { success: false, message: response?.message || 'Failed to receive materials' };
        } catch (error) {
            console.error('Error in createReceive:', error);
            return { success: false, message: error.message || 'Failed to receive materials' };
        }
    },

    // ==================== RAW MATERIALS MASTER ====================
    getRawMaterials: async (params = {}) => {
        try {
            const response = await rawMaterialAPI.getAll(params);
            if (response?.success === true) {
                return {
                    success: true,
                    data: response.data || [],
                    total: response.total || 0,
                    page: response.page || 1,
                    totalPages: response.total_pages || response.totalPages || 1,
                };
            }
            return { success: false, message: response?.message || 'Failed to fetch raw materials', data: [] };
        } catch (error) {
            console.error('Error in getRawMaterials:', error);
            return { success: false, message: error.message || 'Failed to fetch raw materials', data: [] };
        }
    },

    getRawMaterial: async (id) => {
        try {
            const response = await rawMaterialAPI.get(id);
            if (response?.success === true) {
                return { success: true, data: response.data };
            }
            return { success: false, message: response?.message || 'Failed to fetch raw material' };
        } catch (error) {
            console.error('Error in getRawMaterial:', error);
            return { success: false, message: error.message || 'Failed to fetch raw material' };
        }
    },

    createRawMaterial: async (materialData) => {
        try {
            if (!materialData.material_code) {
                return { success: false, message: 'Material code is required' };
            }
            if (!materialData.material_name) {
                return { success: false, message: 'Material name is required' };
            }
            if (!materialData.sku) {
                return { success: false, message: 'SKU is required' };
            }

            const response = await rawMaterialAPI.create(materialData);
            if (response?.success === true) {
                return { success: true, data: response.data, message: response.message || 'Raw material created successfully' };
            }
            return { success: false, message: response?.message || 'Failed to create raw material' };
        } catch (error) {
            console.error('Error in createRawMaterial:', error);
            return { success: false, message: error.message || 'Failed to create raw material' };
        }
    },

    updateRawMaterial: async (id, materialData) => {
        try {
            const response = await rawMaterialAPI.update(id, materialData);
            if (response?.success === true) {
                return { success: true, message: response.message || 'Raw material updated successfully' };
            }
            return { success: false, message: response?.message || 'Failed to update raw material' };
        } catch (error) {
            console.error('Error in updateRawMaterial:', error);
            return { success: false, message: error.message || 'Failed to update raw material' };
        }
    },

    deleteRawMaterial: async (id) => {
        try {
            const response = await rawMaterialAPI.delete(id);
            if (response?.success === true) {
                return { success: true, message: response.message || 'Raw material deleted successfully' };
            }
            return { success: false, message: response?.message || 'Failed to delete raw material' };
        } catch (error) {
            console.error('Error in deleteRawMaterial:', error);
            return { success: false, message: error.message || 'Failed to delete raw material' };
        }
    },

    // ==================== RAW MATERIAL PURCHASE ====================
    createRawMaterialPurchase: async (purchaseData) => {
        try {
            if (!purchaseData.supplier_id) {
                return { success: false, message: 'Supplier is required' };
            }
            if (!purchaseData.items || purchaseData.items.length === 0) {
                return { success: false, message: 'At least one item is required' };
            }

            const response = await rawMaterialAPI.createPurchase(purchaseData);
            if (response?.success === true) {
                return { success: true, data: response.data, message: response.message || 'Purchase created successfully' };
            }
            return { success: false, message: response?.message || 'Failed to create purchase' };
        } catch (error) {
            console.error('Error in createRawMaterialPurchase:', error);
            return { success: false, message: error.message || 'Failed to create purchase' };
        }
    },

    getRawMaterialPurchases: async (params = {}) => {
        try {
            const response = await rawMaterialAPI.getPurchases(params);
            if (response?.success === true) {
                return {
                    success: true,
                    data: response.data || [],
                    total: response.total || 0,
                    page: response.page || 1,
                    totalPages: response.total_pages || response.totalPages || 1,
                };
            }
            return { success: false, message: response?.message || 'Failed to fetch purchases', data: [] };
        } catch (error) {
            console.error('Error in getRawMaterialPurchases:', error);
            return { success: false, message: error.message || 'Failed to fetch purchases', data: [] };
        }
    },

    getRawMaterialPurchase: async (id) => {
        try {
            const response = await rawMaterialAPI.getPurchase(id);
            if (response?.success === true) {
                return { success: true, data: response.data };
            }
            return { success: false, message: response?.message || 'Failed to fetch purchase details' };
        } catch (error) {
            console.error('Error in getRawMaterialPurchase:', error);
            return { success: false, message: error.message || 'Failed to fetch purchase details' };
        }
    },


    updateRawMaterialPurchase: async (id, purchaseData) => {
        try {
            if (!purchaseData.supplier_id) {
                return { success: false, message: 'Supplier is required' };
            }
            if (!purchaseData.items || purchaseData.items.length === 0) {
                return { success: false, message: 'At least one item is required' };
            }
            const response = await rawMaterialAPI.updatePurchase(id, purchaseData);
            if (response?.success === true) {
                return { success: true, message: response.message || 'Purchase updated successfully' };
            }
            return { success: false, message: response?.message || 'Failed to update purchase' };
        } catch (error) {
            console.error('Error in updateRawMaterialPurchase:', error);
            return { success: false, message: error.message || 'Failed to update purchase' };
        }
    },

    deleteRawMaterialPurchase: async (id) => {
        try {
            const response = await rawMaterialAPI.deletePurchase(id);
            if (response?.success === true) {
                return { success: true, message: response.message || 'Purchase deleted successfully' };
            }
            return { success: false, message: response?.message || 'Failed to delete purchase' };
        } catch (error) {
            console.error('Error in deleteRawMaterialPurchase:', error);
            return { success: false, message: error.message || 'Failed to delete purchase' };
        }
    },

    // ==================== PRODUCTION ====================
    getProductions: async (params = {}) => {
        try {
            const response = await manufacturingAPI.getProductions(params);
            if (response?.success === true) {
                return {
                    success: true,
                    data: response.data || [],
                    total: response.total || 0,
                    page: response.page || 1,
                    totalPages: response.total_pages || response.totalPages || 1,
                };
            }
            return { success: false, message: response?.message || 'Failed to fetch productions', data: [] };
        } catch (error) {
            console.error('Error in getProductions:', error);
            return { success: false, message: error.message || 'Failed to fetch productions', data: [] };
        }
    },

    getProduction: async (id) => {
        try {
            const response = await manufacturingAPI.getProduction(id);
            if (response?.success === true) {
                return { success: true, data: response.data };
            }
            return { success: false, message: response?.message || 'Failed to fetch production' };
        } catch (error) {
            console.error('Error in getProduction:', error);
            return { success: false, message: error.message || 'Failed to fetch production' };
        }
    },

    createProduction: async (productionData) => {
        try {
            if (!productionData.production_date) {
                return { success: false, message: 'Production date is required' };
            }
            if (!productionData.batch_no) {
                return { success: false, message: 'Batch number is required' };
            }
            if (!productionData.raw_materials || productionData.raw_materials.length === 0) {
                return { success: false, message: 'At least one raw material is required' };
            }
            if (!productionData.finished_goods || productionData.finished_goods.length === 0) {
                return { success: false, message: 'At least one finished good is required' };
            }

            const response = await manufacturingAPI.createProduction(productionData);
            if (response?.success === true) {
                return { success: true, data: response.data, message: response.message || 'Production created successfully' };
            }
            return { success: false, message: response?.message || 'Failed to create production' };
        } catch (error) {
            console.error('Error in createProduction:', error);
            return { success: false, message: error.message || 'Failed to create production' };
        }
    },

    updateProductionStatus: async (id, status) => {
        try {
            const response = await manufacturingAPI.updateProductionStatus(id, status);
            if (response?.success === true) {
                return { success: true, message: response.message || 'Status updated successfully' };
            }
            return { success: false, message: response?.message || 'Failed to update status' };
        } catch (error) {
            console.error('Error in updateProductionStatus:', error);
            return { success: false, message: error.message || 'Failed to update status' };
        }
    },
    // ==================== PRODUCTION (Update & Delete) ====================
    updateProduction: async (id, productionData) => {
        try {
            if (!productionData.batch_no) {
                return { success: false, message: 'Batch number is required' };
            }
            const response = await manufacturingAPI.updateProduction(id, productionData);
            if (response?.success === true) {
                return { success: true, message: response.message || 'Production updated successfully' };
            }
            return { success: false, message: response?.message || 'Failed to update production' };
        } catch (error) {
            console.error('Error in updateProduction:', error);
            return { success: false, message: error.message || 'Failed to update production' };
        }
    },

    deleteProduction: async (id) => {
        try {
            const response = await manufacturingAPI.deleteProduction(id);
            if (response?.success === true) {
                return { success: true, message: response.message || 'Production deleted successfully' };
            }
            return { success: false, message: response?.message || 'Failed to delete production' };
        } catch (error) {
            console.error('Error in deleteProduction:', error);
            return { success: false, message: error.message || 'Failed to delete production' };
        }
    },

    // ==================== STOCK TRANSFER ====================
    getTransfers: async (params = {}) => {
        try {
            const response = await manufacturingAPI.getTransfers(params);
            if (response?.success === true) {
                return {
                    success: true,
                    data: response.data || [],
                    total: response.total || 0,
                    page: response.page || 1,
                    totalPages: response.total_pages || response.totalPages || 1,
                };
            }
            return { success: false, message: response?.message || 'Failed to fetch transfers', data: [] };
        } catch (error) {
            console.error('Error in getTransfers:', error);
            return { success: false, message: error.message || 'Failed to fetch transfers', data: [] };
        }
    },

    getTransfer: async (id) => {
        try {
            const response = await manufacturingAPI.getTransfer(id);
            if (response?.success === true) {
                return { success: true, data: response.data };
            }
            return { success: false, message: response?.message || 'Failed to fetch transfer' };
        } catch (error) {
            console.error('Error in getTransfer:', error);
            return { success: false, message: error.message || 'Failed to fetch transfer' };
        }
    },

    createTransfer: async (transferData) => {
        try {
            if (!transferData.transfer_date) {
                return { success: false, message: 'Transfer date is required' };
            }
            if (!transferData.from_location) {
                return { success: false, message: 'Source location is required' };
            }
            if (!transferData.to_location) {
                return { success: false, message: 'Destination location is required' };
            }
            if (transferData.from_location === transferData.to_location) {
                return { success: false, message: 'Source and destination cannot be the same' };
            }
            if (!transferData.items || transferData.items.length === 0) {
                return { success: false, message: 'At least one item is required' };
            }

            const response = await manufacturingAPI.createTransfer(transferData);
            if (response?.success === true) {
                return { success: true, data: response.data, message: response.message || 'Transfer created successfully' };
            }
            return { success: false, message: response?.message || 'Failed to create transfer' };
        } catch (error) {
            console.error('Error in createTransfer:', error);
            return { success: false, message: error.message || 'Failed to create transfer' };
        }
    },

    updateTransferStatus: async (id, status) => {
        try {
            if (!['completed', 'cancelled'].includes(status)) {
                return { success: false, message: 'Status must be completed or cancelled' };
            }
            const response = await manufacturingAPI.updateTransferStatus(id, status);
            if (response?.success === true) {
                return { success: true, message: response.message || 'Transfer status updated successfully' };
            }
            return { success: false, message: response?.message || 'Failed to update transfer status' };
        } catch (error) {
            console.error('Error in updateTransferStatus:', error);
            return { success: false, message: error.message || 'Failed to update transfer status' };
        }
    },

    // In manufacturingAction.js - Fix the updateTransfer function

    updateTransfer: async (id, transferData) => {
        try {
            if (!transferData.from_location) {
                return { success: false, message: 'Source location is required' };
            }
            if (!transferData.to_location) {
                return { success: false, message: 'Destination location is required' };
            }
            if (transferData.from_location === transferData.to_location) {
                return { success: false, message: 'Source and destination cannot be the same' };
            }
            if (!transferData.items || transferData.items.length === 0) {
                return { success: false, message: 'At least one item is required' };
            }

            // Filter out empty items
            const validItems = transferData.items.filter(item => item.product_id && item.quantity > 0);
            if (validItems.length === 0) {
                return { success: false, message: 'At least one valid item with quantity > 0 is required' };
            }

            const payload = {
                id: id,
                transfer_date: transferData.transfer_date,
                from_location: transferData.from_location,
                to_location: transferData.to_location,
                transfer_type: transferData.transfer_type,
                notes: transferData.notes || '',
                items: validItems.map(item => ({
                    product_id: item.product_id,
                    quantity: parseFloat(item.quantity),
                    unit: item.unit || 'PCS',
                    unit_cost: parseFloat(item.unit_cost) || 0,
                    remarks: item.remarks || ''
                }))
            };

            const response = await manufacturingAPI.updateTransfer(id, payload);

            if (response?.success === true) {
                return {
                    success: true,
                    data: response.data,
                    message: response.message || 'Transfer updated successfully'
                };
            }
            return { success: false, message: response?.message || 'Failed to update transfer' };
        } catch (error) {
            console.error('Error in updateTransfer:', error);
            return { success: false, message: error.message || 'Failed to update transfer' };
        }
    },

    // ==================== WASTE ENTRY ====================
    getWasteEntries: async (params = {}) => {
        try {
            const response = await manufacturingAPI.getWasteEntries(params);
            if (response?.success === true) {
                return {
                    success: true,
                    data: response.data || [],
                    total: response.total || 0,
                    page: response.page || 1,
                    totalPages: response.total_pages || response.totalPages || 1,
                };
            }
            return { success: false, message: response?.message || 'Failed to fetch waste entries', data: [] };
        } catch (error) {
            console.error('Error in getWasteEntries:', error);
            return { success: false, message: error.message || 'Failed to fetch waste entries', data: [] };
        }
    },

    getWasteEntry: async (id) => {
        try {
            const response = await manufacturingAPI.getWasteEntry(id);
            if (response?.success === true) {
                return { success: true, data: response.data };
            }
            return { success: false, message: response?.message || 'Failed to fetch waste entry' };
        } catch (error) {
            console.error('Error in getWasteEntry:', error);
            return { success: false, message: error.message || 'Failed to fetch waste entry' };
        }
    },

    createWasteEntry: async (wasteData) => {
        try {
            if (!wasteData.waste_date) {
                return { success: false, message: 'Waste date is required' };
            }
            if (!wasteData.production_id) {
                return { success: false, message: 'Production ID is required' };
            }
            if (!wasteData.items || wasteData.items.length === 0) {
                return { success: false, message: 'At least one waste item is required' };
            }

            const response = await manufacturingAPI.createWasteEntry(wasteData);
            if (response?.success === true) {
                return { success: true, data: response.data, message: response.message || 'Waste entry created successfully' };
            }
            return { success: false, message: response?.message || 'Failed to create waste entry' };
        } catch (error) {
            console.error('Error in createWasteEntry:', error);
            return { success: false, message: error.message || 'Failed to create waste entry' };
        }
    },

    updateWasteEntry: async (productionId, wasteData) => {
        try {
            if (!wasteData.waste_date) {
                return { success: false, message: 'Waste date is required' };
            }
            if (!wasteData.items || wasteData.items.length === 0) {
                return { success: false, message: 'At least one waste item is required' };
            }
            const response = await manufacturingAPI.updateWasteEntry(productionId, wasteData);
            if (response?.success === true) {
                return { success: true, data: response.data, message: response.message || 'Waste entry updated successfully' };
            }
            return { success: false, message: response?.message || 'Failed to update waste entry' };
        } catch (error) {
            console.error('Error in updateWasteEntry:', error);
            return { success: false, message: error.message || 'Failed to update waste entry' };
        }
    },

    deleteWasteEntry: async (productionId) => {
        try {
            const response = await manufacturingAPI.deleteWasteEntry(productionId);
            if (response?.success === true) {
                return { success: true, message: response.message || 'Waste entry deleted successfully' };
            }
            return { success: false, message: response?.message || 'Failed to delete waste entry' };
        } catch (error) {
            console.error('Error in deleteWasteEntry:', error);
            return { success: false, message: error.message || 'Failed to delete waste entry' };
        }
    },

    // ==================== CONVERT TO PURCHASE ====================
    convertToPurchase: async (conversionData) => {
        try {
            if (!conversionData.production_id) {
                return { success: false, message: 'Production ID is required' };
            }

            const response = await manufacturingAPI.convertToPurchase(conversionData);
            if (response?.success === true) {
                return { success: true, data: response.data, message: response.message || 'Converted to purchase successfully' };
            }
            return { success: false, message: response?.message || 'Failed to convert production' };
        } catch (error) {
            console.error('Error in convertToPurchase:', error);
            return { success: false, message: error.message || 'Failed to convert production' };
        }
    },

    getUnconvertedProductions: async (params = {}) => {
        try {
            const response = await manufacturingAPI.getUnconvertedProductions(params);
            if (response?.success === true) {
                return {
                    success: true,
                    data: response.data || [],
                    total: response.total || 0,
                    page: response.page || 1,
                    totalPages: response.total_pages || response.totalPages || 1,
                };
            }
            return { success: false, message: response?.message || 'Failed to fetch unconverted productions', data: [] };
        } catch (error) {
            console.error('Error in getUnconvertedProductions:', error);
            return { success: false, message: error.message || 'Failed to fetch unconverted productions', data: [] };
        }
    },

    // ==================== MANUFACTURING STOCK ====================
    getRawMaterialManufacturingStock: async (params = {}) => {
        try {
            const response = await rawMaterialAPI.getManufacturingStock(params);
            if (response?.success === true) {
                return {
                    success: true,
                    data: response.data || [],
                    total: response.total || 0,
                };
            }
            return { success: false, message: response?.message || 'Failed to fetch manufacturing stock', data: [] };
        } catch (error) {
            console.error('Error in getRawMaterialManufacturingStock:', error);
            return { success: false, message: error.message || 'Failed to fetch manufacturing stock', data: [] };
        }
    },

    getFinishedGoodsManufacturingStock: async (params = {}) => {
        try {
            const response = await manufacturingAPI.getFinishedGoodsStock(params);
            if (response?.success === true) {
                return {
                    success: true,
                    data: response.data || [],
                    total: response.total || 0,
                };
            }
            return { success: false, message: response?.message || 'Failed to fetch finished goods stock', data: [] };
        } catch (error) {
            console.error('Error in getFinishedGoodsManufacturingStock:', error);
            return { success: false, message: error.message || 'Failed to fetch finished goods stock', data: [] };
        }
    },



    // ==================== MANUFACTURING STOCK ====================
    getRawMaterialStock: async (params = {}) => {
        try {
            const response = await manufacturingAPI.getRawMaterialStock(params);
            debugger;
            if (response?.success === true) {
                return {
                    success: true,
                    data: response.data || [],
                    total: response.total || 0,
                    page: response.page || 1,
                    total_pages: response.total_pages || Math.ceil((response.total || 0) / (params.limit || 10))
                };
            }
            return { success: false, message: response?.message || 'Failed to fetch raw material stock', data: [] };
        } catch (error) {
            console.error('Error in getRawMaterialStock:', error);
            return { success: false, message: error.message || 'Failed to fetch raw material stock', data: [] };
        }
    },

    getFinishedGoodsStock: async (params = {}) => {
        try {
            const response = await manufacturingAPI.getFinishedGoodsStock(params);
            if (response?.success === true) {
                return {
                    success: true,
                    data: response.data || [],
                    total: response.total || 0,
                    page: response.page || 1,
                    total_pages: response.total_pages || Math.ceil((response.total || 0) / (params.limit || 10))
                };
            }
            return { success: false, message: response?.message || 'Failed to fetch finished goods stock', data: [] };
        } catch (error) {
            console.error('Error in getFinishedGoodsStock:', error);
            return { success: false, message: error.message || 'Failed to fetch finished goods stock', data: [] };
        }
    },
    // ==================== MANUFACTURING REPORTS ====================
    getManufacturingSummary: async (params = {}) => {
        try {
            const response = await manufacturingAPI.getManufacturingSummary(params);
            if (response?.success === true) {
                return { success: true, data: response.data || {} };
            }
            return { success: false, message: response?.message || 'Failed to fetch summary', data: {} };
        } catch (error) {
            console.error('Error in getManufacturingSummary:', error);
            return { success: false, message: error.message || 'Failed to fetch summary', data: {} };
        }
    },

    getProductionReport: async (params = {}) => {
        try {
            const response = await manufacturingAPI.getProductionReport(params);
            if (response?.success === true) {
                return { success: true, data: response.data || [] };
            }
            return { success: false, message: response?.message || 'Failed to fetch production report', data: [] };
        } catch (error) {
            console.error('Error in getProductionReport:', error);
            return { success: false, message: error.message || 'Failed to fetch production report', data: [] };
        }
    },

    getWasteReport: async (params = {}) => {
        try {
            const response = await manufacturingAPI.getWasteReport(params);
            if (response?.success === true) {
                return { success: true, data: response.data || [] };
            }
            return { success: false, message: response?.message || 'Failed to fetch waste report', data: [] };
        } catch (error) {
            console.error('Error in getWasteReport:', error);
            return { success: false, message: error.message || 'Failed to fetch waste report', data: [] };
        }
    },

    // ==================== HELPER FUNCTIONS ====================
    getLocationOptions: () => [
        { value: 'Head Office', label: 'Head Office' },
        { value: 'Manufacturing Unit 1', label: 'Manufacturing Unit 1' },
        { value: 'Manufacturing Unit 2', label: 'Manufacturing Unit 2' },
        { value: 'Warehouse', label: 'Warehouse' },
    ],

    hasManufacturingPermission: (permissionSlug) => {
        try {
            const user = JSON.parse(localStorage.getItem('user') || '{}');
            if (user.role_slug === 'super-admin') return true;
            const permissions = user.permissions || [];
            return Array.isArray(permissions) ? permissions.includes(permissionSlug) : false;
        } catch {
            return false;
        }
    },

    getStatusBadgeClass: (status) => {
        const map = {
            pending: 'badge-warning',
            dispatched: 'badge-primary',
            received: 'badge-success',
            partially_received: 'badge-info',
            cancelled: 'badge-danger',
            completed: 'badge-success',
            draft: 'badge-secondary',
            active: 'badge-success',
            inactive: 'badge-secondary'
        };
        return map[status?.toLowerCase()] || 'badge-secondary';
    },

    generateBatchNo: () => {
        const d = new Date();
        const ymd = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
        return `BATCH-${ymd}-${String(Math.floor(Math.random() * 10000)).padStart(4, '0')}`;
    },
};

export default manufacturingActions;